import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  API_DELETE,
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import { hasPermissions } from '../../auth/hasPermissions';
import Cookies from 'universal-cookie';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import {
  playNotificationSound,
  playInChatSound,
  playUrgentSound,
  primeAudioContextOnGesture,
} from './useChatAudio';

// Make Pusher available globally for Laravel Echo
window.Pusher = Pusher;

const cookies = new Cookies();

// Dev-only logger — silent in production
const isDev = process.env.NODE_ENV !== 'production';
const chatLog = (...args) => {
  if (isDev) console.log(...args);
};
const chatWarn = (...args) => {
  if (isDev) console.warn(...args);
};

// Helper: is the user actively viewing this conversation right now?
// Considers tab visibility AND window focus, not just URL.
const isActivelyViewing = (selectedConvId, conversationId) => {
  if (!selectedConvId || !conversationId) return false;
  if (String(selectedConvId) !== String(conversationId)) return false;
  if (typeof document !== 'undefined') {
    if (document.visibilityState !== 'visible') return false;
    if (typeof document.hasFocus === 'function' && !document.hasFocus())
      return false;
  }
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return path.startsWith('/chat');
};

// Merge messages without duplicates.
// Dedupe is done against the list itself (not an external Set) so the state updater
// stays pure — React may call an updater more than once, and a version that mutated a
// ref would drop the message on the second call.
const mergeMessages = (base, incoming, position = 'prepend') => {
  const existingIds = new Set(base.map((m) => m.id));
  const fresh = incoming.filter((m) => m && !existingIds.has(m.id));
  if (fresh.length === 0) return base;
  return position === 'prepend' ? [...fresh, ...base] : [...base, ...fresh];
};

const MESSAGES_PAGE_SIZE = 50;
// Half a page either side when jumping to a search hit
const JUMP_CONTEXT_SIZE = 25;

// The message list is always newest-first. Sorting each incoming page keeps that true
// no matter which order the server returns a given direction in.
const sortNewestFirst = (list) =>
  [...list].sort((a, b) => {
    const diff = new Date(b.createdAt) - new Date(a.createdAt);
    // Same timestamp: fall back to id so the order stays stable
    return diff !== 0 ? diff : Number(b.id) - Number(a.id);
  });

// figures out where to continue loading messages and whether there are probably more messages to load.
const resolvePage = (page, meta, perPage, direction) => {
  if (meta.cursor !== null && meta.cursor !== undefined) {
    return { cursor: meta.cursor, hasMore: meta.hasMore };
  }
  // page is newest-first: going "after" continues from its newest, "before" from its oldest
  const edge = direction === 'after' ? page[0] : page[page.length - 1];
  return {
    cursor: edge?.id ?? null,
    hasMore: Boolean(edge) && page.length >= perPage,
  };
};

// Connection status constants
const CONNECTION_STATUS = {
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  DISCONNECTED: 'disconnected',
  FAILED: 'failed',
};

// Use WebSocket normally, but check more frequently with polling when WebSocket is broken.
const POLL_INTERVAL_CONNECTED = 30000;
const POLL_INTERVAL_DEGRADED = 10000;

// How long a message that arrives in the conversation you are already looking at stays
// marked unread in the sidebar before it is read automatically.
const AUTO_READ_DELAY_MS = 3000;

// Reconnection config
const RECONNECT_CONFIG = {
  maxAttempts: 5,
  baseDelay: 1000,
  maxDelay: 30000,
};

const ChatClientContext = createContext({
  conversations: [],
  selectedConversation: null,
  messages: [],
  unreadTotal: 0,
  isLoading: false,
  typingUsersByConversation: {},
  notifications: [],
  replyToMessage: null,
  pendingFiles: null,
  connectionStatus: CONNECTION_STATUS.DISCONNECTED,
  getEcho: () => null,
  setSelectedConversation: () => {},
  sendMessage: () => {},
  loadMoreMessages: () => {},
  createConversation: () => {},
  findOrCreateDirect: () => {},
  markAsRead: () => {},
  refreshConversations: () => {},
  dismissNotification: () => {},
  setReplyToMessage: () => {},
  addReaction: () => {},
  removeReaction: () => {},
  updateConversation: () => {},
  setPendingFiles: () => {},
});

export const ChatClientProvider = ({ children }) => {
  const config = useConfig();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Compute unreadTotal from conversations - always in sync
  const unreadTotal = useMemo(() => {
    return conversations.reduce(
      (sum, conv) => sum + (conv.unreadCount || 0),
      0,
    );
  }, [conversations]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  // Paging runs in two directions. "older" is the normal scroll-up case. "newer" only
  // matters after jumping into the middle of history from a search result.
  const [hasMoreMessages, setHasMoreMessages] = useState(true); // older
  const [olderCursor, setOlderCursor] = useState(null);
  const [hasMoreNewer, setHasMoreNewer] = useState(false);
  const [newerCursor, setNewerCursor] = useState(null);
  // Number of live messages that arrived while the user was reading old history. They are
  // deliberately not inserted — they belong at the bottom of the conversation, not next to
  // messages from months ago — so we just count them for the "jump to latest" badge.
  const [pendingNewCount, setPendingNewCount] = useState(0);
  // Set briefly after a jump so the list can scroll to and flash that message
  const [pendingScrollToId, setPendingScrollToId] = useState(null);
  // Which way we are currently paging: 'older' | 'newer' | 'initial' | null.
  // The list needs this to put the spinner at the right end.
  const [loadingDirection, setLoadingDirection] = useState(null);

  // True when the list is anchored to the newest message, i.e. normal chat mode.
  const isAtLatest = !hasMoreNewer;
  const [typingUsersByConversation, setTypingUsersByConversation] = useState(
    {},
  );
  const [notifications, setNotifications] = useState([]);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [pendingFiles, setPendingFiles] = useState(null);
  const [urgentMessage, setUrgentMessage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(
    CONNECTION_STATUS.DISCONNECTED,
  );
  const [, setReconnectAttempts] = useState(0); // Value read only via functional updates
  const typingTimeoutRef = useRef({});
  const notificationIdRef = useRef(0);
  const notificationTimeoutsRef = useRef(new Map());
  const echoRef = useRef(null);
  // shares the connection with other realtime features (e.g. the AI chat)
  const getEcho = useCallback(() => echoRef.current, []);
  const subscribedConversationsRef = useRef(new Set());
  const selectedConversationRef = useRef(null);
  const conversationsRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);
  const messagesRequestRef = useRef(0);
  // Generation of the message fetch whose result is currently in `messages`. Equal to
  // messagesRequestRef only when the list is settled for the selected conversation.
  const settledRequestRef = useRef(-1);
  const reconciledMessageIdRef = useRef(null);
  const autoReadMessageIdRef = useRef(null);

  // Check if user is authenticated
  const isAuthenticated = Boolean(cookies.get('accessToken'));
  const currentUserId = cookies.get('userId');

  // Keep refs in sync with state for WebSocket handlers (avoid stale closures)
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // Websocket handlers run from an old closure, so they read this instead of the value
  const isAtLatestRef = useRef(true);
  useEffect(() => {
    isAtLatestRef.current = isAtLatest;
  }, [isAtLatest]);

  // Memoized conversation IDs string for efficient dependency tracking
  const conversationIdsKey = useMemo(
    () => conversations.map((c) => c.id).join(','),
    [conversations],
  );

  // Mark all messages as read (defined early because it's used in the messages useEffect)
  const markAllAsRead = useCallback(
    (conversationId) => {
      if (!config || !conversationId) return;

      const convIdStr = String(conversationId);

      // Store previous unread count for potential rollback
      const previousUnreadCount =
        conversationsRef.current.find((conv) => String(conv.id) === convIdStr)
          ?.unreadCount || 0;

      // Update local state immediately for responsive UI (optimistic update)
      // unreadTotal is computed automatically via useMemo from conversations
      setConversations((prev) => {
        return prev.map((conv) => {
          // Use string comparison to avoid type mismatch issues
          if (String(conv.id) === convIdStr) {
            return { ...conv, unreadCount: 0 };
          }
          return conv;
        });
      });

      // Then make the API call
      DBRequest({
        config,
        path: `chat/conversations/${conversationId}/read-all`,
        method: API_POST({}),
        onError: (err) => {
          console.error('[Chat] Failed to mark messages as read:', err);
          // Rollback on failure
          setConversations((prev) => {
            return prev.map((conv) => {
              if (String(conv.id) === convIdStr) {
                return { ...conv, unreadCount: previousUnreadCount };
              }
              return conv;
            });
          });
        },
      });
    },
    [config],
  );

  // Fetch conversations
  const hasChatPermission = hasPermissions('chat-message');

  const fetchConversations = useCallback(() => {
    if (!config || !isAuthenticated || !hasChatPermission) return;

    DBRequest({
      config,
      path: 'chat/conversations?perPage=50',
      method: API_GET,
      onResponse: (res) => {
        setConversations(res.data || []);
        // unreadTotal is computed automatically via useMemo
        setIsLoadingConversations(false);
      },
      onError: () => {
        setIsLoadingConversations(false);
      },
    });
  }, [config, isAuthenticated, hasChatPermission]);

  // Initial fetch and polling
  useEffect(() => {
    if (!config || !isAuthenticated || !hasChatPermission) return;

    fetchConversations();

    // Poll for new conversations — faster while the websocket is not connected, since
    // then this is the only thing keeping the conversation list (and, via the sync
    // effect below, the open conversation's messages) up to date.
    const interval = setInterval(
      fetchConversations,
      connectionStatus === CONNECTION_STATUS.CONNECTED
        ? POLL_INTERVAL_CONNECTED
        : POLL_INTERVAL_DEGRADED,
    );
    return () => clearInterval(interval);
  }, [
    config,
    fetchConversations,
    isAuthenticated,
    hasChatPermission,
    connectionStatus,
  ]);

  // Prime audio context on first user gesture — required by iOS Safari & browser autoplay policy
  useEffect(() => {
    primeAudioContextOnGesture();
  }, []);

  // When tab becomes visible / window regains focus while a conversation is open,
  // mark messages as read on the server (we may have missed an auto-read while hidden).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handleVisible = () => {
      const conv = selectedConversationRef.current;
      if (!conv) return;
      if (isActivelyViewing(conv.id, conv.id)) {
        markAllAsRead(conv.id);
      }
    };
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
    };
  }, [markAllAsRead]);

  // Listen for standalone auth changes (re-login)
  useEffect(() => {
    const handleAuthChange = (event) => {
      if (event.detail?.authenticated && config) {
        // Small delay to ensure cookies are set
        setTimeout(() => {
          setIsLoadingConversations(true);
          fetchConversations();
        }, 100);
      }
    };

    window.addEventListener('standaloneAuthChange', handleAuthChange);
    return () =>
      window.removeEventListener('standaloneAuthChange', handleAuthChange);
  }, [config, fetchConversations]);

  // Back to "normal chat" paging state: anchored to the newest message, nothing buffered
  const resetPaging = useCallback(() => {
    setHasMoreMessages(true);
    setOlderCursor(null);
    setHasMoreNewer(false);
    setNewerCursor(null);
    setPendingNewCount(0);
  }, []);

  // One place that talks to the messages endpoint, so every caller gets the same
  // stale-response guard, ordering and error handling.
  const fetchMessagePage = useCallback(
    ({ conversationId, cursor, direction, requestId, onPage }) => {
      if (!config || !conversationId) return;

      const params = new URLSearchParams({
        perPage: String(MESSAGES_PAGE_SIZE),
      });
      if (cursor) params.set('cursor', String(cursor));
      if (direction) params.set('direction', direction);

      setIsLoading(true);
      setLoadingDirection(
        direction === 'after'
          ? 'newer'
          : direction === 'before'
            ? 'older'
            : 'initial',
      );
      DBRequest({
        config,
        path: `chat/conversations/${conversationId}/messages?${params.toString()}`,
        method: API_GET,
        onResponse: (res) => {
          setLoadingDirection(null);
          // Drop the response if the user switched conversation while it was in flight
          if (messagesRequestRef.current !== requestId) return;
          // Sort defensively: the list is newest-first, and we do not want to depend on
          // the server returning "after" pages in the same order as "before" pages.
          const page = sortNewestFirst(res.data || []);
          const meta = {
            hasMore: Boolean(res.meta?.hasMore),
            cursor: res.meta?.nextCursor ?? null,
          };
          onPage(
            page,
            resolvePage(page, meta, MESSAGES_PAGE_SIZE, direction || 'before'),
          );
          setIsLoading(false);
        },
        onError: (err) => {
          console.error('[Chat] Failed to load messages:', err);
          setLoadingDirection(null);
          if (messagesRequestRef.current !== requestId) return;
          setIsLoading(false);
        },
      });
    },
    [config],
  );

  // Fetch messages when conversation changes
  useEffect(() => {
    reconciledMessageIdRef.current = null;

    if (!config || !selectedConversation) {
      messagesRequestRef.current++;
      resetPaging();
      setMessages([]);
      return;
    }

    // Invalidates any request still in flight for a previously selected conversation,
    // so a slow response can't land in the wrong conversation's message list.
    const requestId = ++messagesRequestRef.current;

    // Clear messages immediately when switching conversation
    setMessages([]);
    setIsLoading(true);
    resetPaging();

    fetchMessagePage({
      conversationId: selectedConversation.id,
      requestId,
      onPage: (page, meta) => {
        settledRequestRef.current = requestId;
        // Keep anything that arrived over the websocket while this fetch was in flight
        // instead of overwriting it with the (already stale) server page.
        setMessages((prev) => mergeMessages(page, prev, 'prepend'));
        setHasMoreMessages(meta.hasMore);
        setOlderCursor(meta.cursor);
      },
    });

    // Mark all as read when opening conversation
    markAllAsRead(selectedConversation.id);
  }, [
    config,
    selectedConversation?.id,
    markAllAsRead,
    fetchMessagePage,
    resetPaging,
  ]);

  // Scroll up: older messages go on the end of the list
  const loadMoreMessages = useCallback(() => {
    if (!selectedConversation || !hasMoreMessages || isLoading || !olderCursor)
      return;

    fetchMessagePage({
      conversationId: selectedConversation.id,
      cursor: olderCursor,
      direction: 'before',
      requestId: messagesRequestRef.current,
      onPage: (page, meta) => {
        setMessages((prev) => mergeMessages(prev, page, 'append'));
        // Stop if the server didn't actually move us on, so the scroll trigger can't
        // re-request the same page forever
        const movedOn = page.length > 0 && meta.cursor !== olderCursor;
        setHasMoreMessages(meta.hasMore && movedOn);
        setOlderCursor(meta.cursor);
      },
    });
  }, [
    selectedConversation,
    hasMoreMessages,
    isLoading,
    olderCursor,
    fetchMessagePage,
  ]);

  // Scroll down while reading history: newer messages go on the front of the list
  const loadNewerMessages = useCallback(() => {
    if (!selectedConversation || !hasMoreNewer || isLoading || !newerCursor)
      return;

    fetchMessagePage({
      conversationId: selectedConversation.id,
      cursor: newerCursor,
      direction: 'after',
      requestId: messagesRequestRef.current,
      onPage: (page, meta) => {
        setMessages((prev) => mergeMessages(prev, page, 'prepend'));
        const movedOn = page.length > 0 && meta.cursor !== newerCursor;
        setHasMoreNewer(meta.hasMore && movedOn);
        setNewerCursor(meta.cursor);
        // Caught up with live — live messages can flow into the list again
        if (!(meta.hasMore && movedOn)) setPendingNewCount(0);
      },
    });
  }, [
    selectedConversation,
    hasMoreNewer,
    isLoading,
    newerCursor,
    fetchMessagePage,
  ]);

  // Open the conversation around one specific message (used by search results).
  // The endpoint pages one way at a time, so we ask for both sides of the message and
  // put the message itself — which the caller already has — in the middle.
  const jumpToMessage = useCallback(
    (message) => {
      const conversationId = selectedConversation?.id;
      if (!config || !conversationId || !message?.id) return;

      const requestId = ++messagesRequestRef.current;
      setMessages([message]);
      setIsLoading(true);
      resetPaging();

      const load = (direction) =>
        new Promise((resolve) => {
          DBRequest({
            config,
            path: `chat/conversations/${conversationId}/messages?perPage=${JUMP_CONTEXT_SIZE}&cursor=${encodeURIComponent(message.id)}&direction=${direction}`,
            method: API_GET,
            onResponse: (res) => {
              const page = sortNewestFirst(res.data || []);
              const meta = {
                hasMore: Boolean(res.meta?.hasMore),
                cursor: res.meta?.nextCursor ?? null,
              };
              resolve({
                page,
                ...resolvePage(page, meta, JUMP_CONTEXT_SIZE, direction),
              });
            },
            onError: () => resolve({ page: [], hasMore: false, cursor: null }),
          });
        });

      Promise.all([load('before'), load('after')]).then(([older, newer]) => {
        if (messagesRequestRef.current !== requestId) return;

        // newest-first: newer messages on top, the target in the middle, older below
        setMessages(sortNewestFirst([...newer.page, message, ...older.page]));
        setHasMoreMessages(older.hasMore);
        setOlderCursor(older.cursor);
        setHasMoreNewer(newer.hasMore);
        setNewerCursor(newer.cursor);
        settledRequestRef.current = requestId;
        setIsLoading(false);
        // Tells the list to scroll to this message and flash it
        setPendingScrollToId(message.id);
      });
    },
    [config, selectedConversation?.id, resetPaging],
  );

  // Leave history and go back to the bottom of the conversation
  const returnToLatest = useCallback(() => {
    const conversationId = selectedConversation?.id;
    if (!config || !conversationId) return;

    const requestId = ++messagesRequestRef.current;
    setMessages([]);
    setIsLoading(true);
    resetPaging();

    fetchMessagePage({
      conversationId,
      requestId,
      onPage: (page, meta) => {
        settledRequestRef.current = requestId;
        setMessages(page);
        setHasMoreMessages(meta.hasMore);
        setOlderCursor(meta.cursor);
      },
    });
  }, [config, selectedConversation?.id, resetPaging, fetchMessagePage]);

  const clearPendingScroll = useCallback(() => setPendingScrollToId(null), []);

  // Send message
  const sendMessage = useCallback(
    (content, options = {}) => {
      if (!config || !selectedConversation)
        return Promise.reject('No conversation selected');

      const {
        attachments = [],
        references = [],
        replyToId = null,
        isUrgent = false,
      } = options;

      return new Promise((resolve, reject) => {
        DBRequest({
          config,
          path: `chat/conversations/${selectedConversation.id}/messages`,
          method: API_POST({
            content,
            attachments,
            references,
            reply_to_id: replyToId,
            is_urgent: isUrgent,
          }),
          onResponse: (res) => {
            // Add new message to the beginning of the list (deduped against the list itself)
            setMessages((prev) => mergeMessages(prev, [res.data], 'prepend'));
            // Update conversation's last message
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === selectedConversation.id
                  ? {
                      ...conv,
                      latestMessage: res.data,
                      lastMessageAt: new Date().toISOString(),
                    }
                  : conv,
              ),
            );
            resolve(res.data);
          },
          onError: reject,
        });
      });
    },
    [config, selectedConversation],
  );

  // Create new conversation
  const createConversation = useCallback(
    (type, participantIds, name = null) => {
      if (!config) return Promise.reject('No config');

      return new Promise((resolve, reject) => {
        DBRequest({
          config,
          path: 'chat/conversations',
          method: API_POST({
            type,
            participant_ids: participantIds,
            name,
          }),
          onResponse: (res) => {
            setConversations((prev) => [res.data, ...prev]);
            setSelectedConversation(res.data);
            resolve(res.data);
          },
          onError: reject,
        });
      });
    },
    [config],
  );

  // Find or create direct message — dedupe in-flight requests for the same user
  const findOrCreateDirectInflightRef = useRef(new Map());

  const findOrCreateDirect = useCallback(
    (userId) => {
      if (!config) return Promise.reject('No config');

      const key = String(userId);
      const inflight = findOrCreateDirectInflightRef.current.get(key);
      if (inflight) return inflight;

      const promise = new Promise((resolve, reject) => {
        DBRequest({
          config,
          path: `chat/conversations/direct/${userId}`,
          method: API_GET,
          onResponse: (res) => {
            const conversation = res.data || res;
            setConversations((prev) => {
              const exists = prev.find((c) => c.id === conversation.id);
              if (exists) return prev;
              return [conversation, ...prev];
            });
            setSelectedConversation(conversation);
            resolve(conversation);
          },
          onError: reject,
        });
      });

      findOrCreateDirectInflightRef.current.set(key, promise);
      promise.finally(() => {
        findOrCreateDirectInflightRef.current.delete(key);
      });
      return promise;
    },
    [config],
  );

  // Mark message as read
  const markAsRead = useCallback(
    (messageId) => {
      if (!config) return;

      DBRequest({
        config,
        path: `chat/messages/${messageId}/read`,
        method: API_PATCH({}),
      });
    },
    [config],
  );

  // Send typing indicator — leading-edge throttled so the receiver sees it immediately
  // on the first keystroke, but we don't spam the API while the user keeps typing.
  const typingThrottleRef = useRef({ lastSentAt: 0, lastConvId: null });
  const TYPING_THROTTLE_MS = 3000;

  const sendTypingIndicator = useCallback(() => {
    if (!config || !selectedConversation) return;
    const convId = selectedConversation.id;
    const now = Date.now();
    const { lastSentAt, lastConvId } = typingThrottleRef.current;
    // Skip if we just sent for this conversation within the throttle window
    if (lastConvId === convId && now - lastSentAt < TYPING_THROTTLE_MS) return;

    typingThrottleRef.current = { lastSentAt: now, lastConvId: convId };
    DBRequest({
      config,
      path: `chat/conversations/${convId}/typing`,
      method: API_POST({}),
      onError: (err) => {
        chatWarn('[Chat] Failed to send typing indicator:', err);
      },
    });
  }, [config, selectedConversation]);

  // Handle incoming typing indicator (per-conversation)
  const handleTypingIndicator = useCallback(
    (conversationId, userId, userName) => {
      if (!conversationId || !userId) return;
      const convKey = String(conversationId);
      const userKey = String(userId);
      const timeoutKey = `${convKey}_${userKey}`;

      setTypingUsersByConversation((prev) => {
        const convUsers = prev[convKey] || {};
        // Avoid update if name didn't change (keeps same ref → no rerender of memo'd children)
        if (convUsers[userKey] === userName) return prev;
        return {
          ...prev,
          [convKey]: { ...convUsers, [userKey]: userName },
        };
      });

      // Clear existing timeout for this (conversation, user)
      if (typingTimeoutRef.current[timeoutKey]) {
        clearTimeout(typingTimeoutRef.current[timeoutKey]);
      }

      // Remove typing indicator after 3 seconds
      typingTimeoutRef.current[timeoutKey] = setTimeout(() => {
        delete typingTimeoutRef.current[timeoutKey];
        setTypingUsersByConversation((prev) => {
          const convUsers = prev[convKey];
          if (!convUsers || !(userKey in convUsers)) return prev;
          const nextConvUsers = { ...convUsers };
          delete nextConvUsers[userKey];
          const next = { ...prev };
          if (Object.keys(nextConvUsers).length === 0) {
            delete next[convKey];
          } else {
            next[convKey] = nextConvUsers;
          }
          return next;
        });
      }, 3000);
    },
    [],
  );

  // Show notification popup
  const showNotification = useCallback((message, conversation) => {
    const notificationId = ++notificationIdRef.current;
    // Get sender name
    const senderName =
      message.sender?.name ||
      `${message.sender?.firstName || ''} ${message.sender?.lastName || ''}`.trim() ||
      message.senderName ||
      'Unknown';
    // Get sender avatar (try temporaryUrl first, then url, then avatar)
    const senderAvatar =
      message.sender?.picture?.temporaryUrl ||
      message.sender?.picture?.url ||
      message.sender?.avatar ||
      message.senderAvatar;

    const notification = {
      id: notificationId,
      conversationId: conversation?.id || message.conversationId,
      conversationName:
        conversation?.type !== 'direct' ? conversation?.name : null,
      senderName,
      senderAvatar,
      content: message.content,
    };

    setNotifications((prev) => [notification, ...prev]);

    // Auto-dismiss after 5 seconds (store timeout for cleanup)
    const timeoutId = setTimeout(() => {
      notificationTimeoutsRef.current.delete(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    }, 5000);
    notificationTimeoutsRef.current.set(notificationId, timeoutId);
  }, []);

  // Dismiss notification
  const dismissNotification = useCallback((notificationId) => {
    // Clear the auto-dismiss timeout if it exists
    const timeoutId = notificationTimeoutsRef.current.get(notificationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      notificationTimeoutsRef.current.delete(notificationId);
    }
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  // Add reaction to message
  const addReaction = useCallback(
    (messageId, emoji) => {
      if (!config) return Promise.reject('No config');

      return new Promise((resolve, reject) => {
        DBRequest({
          config,
          path: `chat/messages/${messageId}/react`,
          method: API_POST({ emoji }),
          onResponse: (res) => {
            // Update with server response
            const reactions = res.data?.reactions || res.reactions || [];
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id === messageId) {
                  return { ...m, reactions };
                }
                return m;
              }),
            );
            resolve(res);
          },
          onError: reject,
        });
      });
    },
    [config],
  );

  // Remove reaction from message
  const removeReaction = useCallback(
    (messageId, emoji) => {
      if (!config) return Promise.reject('No config');

      return new Promise((resolve, reject) => {
        DBRequest({
          config,
          path: `chat/messages/${messageId}/react/${encodeURIComponent(emoji)}`,
          method: API_DELETE(),
          onResponse: (res) => {
            // Update with server response
            const reactions = res.data?.reactions || res.reactions || [];
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id === messageId) {
                  return { ...m, reactions };
                }
                return m;
              }),
            );
            resolve(res);
          },
          onError: reject,
        });
      });
    },
    [config],
  );

  // Update a conversation locally
  const updateConversation = useCallback(
    (conversationId, updates) => {
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === conversationId) {
            return { ...conv, ...updates };
          }
          return conv;
        }),
      );

      // Also update selectedConversation if it's the current one
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation((prev) =>
          prev ? { ...prev, ...updates } : prev,
        );
      }
    },
    [selectedConversation],
  );

  // Dismiss urgent message fullscreen display
  const dismissUrgentMessage = useCallback(() => {
    setUrgentMessage(null);
  }, []);

  // Refresh messages for the current conversation (used to fill gaps)
  const refreshCurrentMessages = useCallback(() => {
    if (!config || !selectedConversationRef.current) return;
    // This pulls the newest page, so it only makes sense when the list is anchored to the
    // bottom. While reading history it would drop recent messages into the middle.
    if (!isAtLatestRef.current) return;
    chatLog(
      '[Chat] Refreshing messages for conversation:',
      selectedConversationRef.current.id,
    );
    const requestId = messagesRequestRef.current;
    DBRequest({
      config,
      path: `chat/conversations/${selectedConversationRef.current.id}/messages?perPage=50`,
      method: API_GET,
      onResponse: (res) => {
        // Drop the response if the user switched conversations while it was in flight
        if (messagesRequestRef.current !== requestId) return;
        // Merge: prepend new messages, keep existing older ones
        setMessages((prev) => mergeMessages(prev, res.data || [], 'prepend'));
      },
      onError: (err) => {
        console.error('[Chat] Failed to refresh messages:', err);
        // Clear the gap marker so a later poll can retry this reconciliation
        if (messagesRequestRef.current === requestId) {
          reconciledMessageIdRef.current = null;
        }
      },
    });
  }, [config]);

  // The conversation-list row for the open conversation — the server-backed view of it
  // (latest message, unread count), as opposed to the snapshot held in
  // `selectedConversation`. Both effects below key off it.
  const selectedConversationRow = useMemo(
    () =>
      conversations.find(
        (c) => String(c.id) === String(selectedConversation?.id),
      ),
    [conversations, selectedConversation?.id],
  );

  // Keep the open conversation's message list in sync with the conversation list.
  // `latestMessage` is written by both the websocket handler and the poll, so if it names
  // a message the open conversation's list doesn't contain, the websocket insert was
  // missed — pull the newest page once instead of leaving the chat area stale until the
  // user switches conversations. In the healthy path this is an id comparison, no request.
  useEffect(() => {
    const conversationId = selectedConversation?.id;
    if (!conversationId) return;
    // Only act once `messages` actually belongs to the selected conversation. Without
    // this, switching conversations would compare the new conversation's latestMessage
    // against the previous conversation's messages and fire a redundant fetch — the
    // initial fetch, which is already in flight, brings the message anyway.
    if (settledRequestRef.current !== messagesRequestRef.current) return;
    // While reading history the newest message is *supposed* to be missing from the list
    if (!isAtLatest) return;

    const latestMessage = selectedConversationRow?.latestMessage;
    if (!latestMessage?.id) return;

    // Already handled this one (either it's in the list, or we already refetched for it)
    if (reconciledMessageIdRef.current === latestMessage.id) return;

    if (messages.some((m) => m.id === latestMessage.id)) {
      reconciledMessageIdRef.current = latestMessage.id;
      return;
    }

    // Mark before fetching so a re-render can't queue a second request for the same gap
    reconciledMessageIdRef.current = latestMessage.id;
    chatWarn(
      '[Chat] Message list out of sync with conversation list — a message.new websocket ' +
        'event was missed for conversation:',
      conversationId,
    );
    refreshCurrentMessages();
  }, [
    selectedConversationRow,
    selectedConversation?.id,
    messages,
    isAtLatest,
    refreshCurrentMessages,
  ]);

  // Auto-mark as read while you are looking at the conversation.
  //
  // A message that arrives in the open conversation surfaces in the sidebar briefly and
  // then clears itself. Without this the unread count the server returns on every poll
  // sticks until the conversation is reopened — replying doesn't clear it either, since
  // sending a message doesn't mark the incoming one read.
  //
  // Deps are primitives on purpose: the timer must not restart every time the poll hands
  // back a new conversation-row object.
  const latestMessageId = selectedConversationRow?.latestMessage?.id;
  const selectedUnreadCount = selectedConversationRow?.unreadCount || 0;

  useEffect(() => {
    const conversationId = selectedConversation?.id;
    if (!conversationId || !latestMessageId || selectedUnreadCount === 0)
      return;
    // Already auto-read this message — don't re-POST if the server is slow to catch up
    if (autoReadMessageIdRef.current === latestMessageId) return;
    // Only if the conversation is genuinely on screen (chat page, tab visible, focused)
    if (!isActivelyViewing(conversationId, conversationId)) return;

    const timeout = setTimeout(() => {
      autoReadMessageIdRef.current = latestMessageId;
      // Re-check on fire — the user may have switched away or backgrounded the tab
      if (
        isActivelyViewing(selectedConversationRef.current?.id, conversationId)
      ) {
        markAllAsRead(conversationId);
      }
    }, AUTO_READ_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [
    selectedConversation?.id,
    latestMessageId,
    selectedUnreadCount,
    markAllAsRead,
  ]);

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback((attempts) => {
    if (attempts >= RECONNECT_CONFIG.maxAttempts) {
      chatLog('[Chat] Max reconnect attempts reached, giving up');
      setConnectionStatus(CONNECTION_STATUS.FAILED);
      return;
    }

    const delay = Math.min(
      RECONNECT_CONFIG.baseDelay * Math.pow(2, attempts),
      RECONNECT_CONFIG.maxDelay,
    );
    chatLog(
      `[Chat] Scheduling reconnect attempt ${attempts + 1} in ${delay}ms`,
    );

    // Clear any previously-scheduled reconnect to avoid stacking timers
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      if (echoRef.current?.connector?.pusher) {
        chatLog('[Chat] Attempting reconnect...');
        setConnectionStatus(CONNECTION_STATUS.CONNECTING);
        echoRef.current.connector.pusher.connect();
      }
    }, delay);
  }, []);

  // Initialize Laravel Echo
  useEffect(() => {
    if (
      !config ||
      !isAuthenticated ||
      !hasChatPermission ||
      !config.pusher?.key
    )
      return;

    // Create Echo instance if not exists
    if (!echoRef.current) {
      chatLog('[Chat] Initializing Echo with config:', config.pusher);
      setConnectionStatus(CONNECTION_STATUS.CONNECTING);

      echoRef.current = new Echo({
        broadcaster: 'pusher',
        key: config.pusher.key,
        cluster: config.pusher.cluster || 'mt1',
        wsHost: config.pusher.wsHost,
        wsPort: config.pusher.wsPort,
        wssPort: config.pusher.wsPort,
        forceTLS: config.pusher.forceTLS === true,
        encrypted: config.pusher.forceTLS === true,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        authEndpoint: `${config.apiUrl}/broadcasting/auth`,
        auth: {
          // Dynamic token getter - always reads fresh token
          headers: {
            get Authorization() {
              return `Bearer ${cookies.get('accessToken')}`;
            },
            Accept: 'application/json',
          },
        },
      });

      const connection = echoRef.current.connector.pusher.connection;

      // Handle successful connection
      connection.bind('connected', () => {
        chatLog('[Chat] WebSocket connected!');
        setConnectionStatus((prev) => {
          // If we were disconnected, refresh data to fill gaps
          if (
            prev === CONNECTION_STATUS.DISCONNECTED ||
            prev === CONNECTION_STATUS.CONNECTING
          ) {
            refreshCurrentMessages();
            fetchConversations();
          }
          return CONNECTION_STATUS.CONNECTED;
        });
        setReconnectAttempts(0);
        // Clear any pending reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      });

      // Handle disconnection - schedule reconnect
      connection.bind('disconnected', () => {
        chatLog('[Chat] WebSocket disconnected');
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        setReconnectAttempts((prev) => {
          scheduleReconnect(prev);
          return prev + 1;
        });
      });

      // Handle unavailable state (couldn't connect at all)
      connection.bind('unavailable', () => {
        chatLog('[Chat] WebSocket unavailable');
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        setReconnectAttempts((prev) => {
          scheduleReconnect(prev);
          return prev + 1;
        });
      });

      // Handle errors
      connection.bind('error', (err) => {
        console.error('[Chat] WebSocket error:', err);
        // Auth errors should trigger immediate reconnect attempt with fresh token
        if (err?.error?.data?.code === 4001 || err?.type === 'AuthError') {
          chatLog('[Chat] Auth error, reconnecting with fresh token...');
          setReconnectAttempts(0);
          scheduleReconnect(0);
        }
      });

      // Debug state changes
      connection.bind('state_change', (states) => {
        chatLog(
          '[Chat] WebSocket state:',
          states.previous,
          '->',
          states.current,
        );
      });
    }

    return () => {
      // Don't disconnect on re-render, only on unmount
    };
  }, [config, isAuthenticated, hasChatPermission, scheduleReconnect]);

  // Disconnect Echo on logout / loss of auth (otherwise old token stays subscribed)
  useEffect(() => {
    if (isAuthenticated && hasChatPermission) return;
    if (!echoRef.current) return;
    chatLog('[Chat] Auth lost — disconnecting Echo');
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    try {
      echoRef.current.disconnect();
    } catch (e) {
      // ignore — already disconnected
    }
    echoRef.current = null;
    subscribedConversationsRef.current.clear();
    setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
    setReconnectAttempts(0);
    // Reset chat state so a new login starts fresh
    setConversations([]);
    setSelectedConversation(null);
    setMessages([]);
    messagesRequestRef.current++;
    setTypingUsersByConversation({});
  }, [isAuthenticated, hasChatPermission]);

  // Subscribe to all conversation channels for real-time updates
  useEffect(() => {
    if (!echoRef.current) return;

    // Get current conversation IDs as a Set for efficient lookup
    const currentConversationIds = new Set(conversations.map((c) => c.id));

    // Unsubscribe from conversations that are no longer in the list
    subscribedConversationsRef.current.forEach((id) => {
      if (!currentConversationIds.has(id)) {
        chatLog('[Chat] Unsubscribing from removed conversation:', id);
        echoRef.current.leave(`conversation.${id}`);
        subscribedConversationsRef.current.delete(id);
      }
    });

    if (conversations.length === 0) return;

    chatLog(
      '[Chat] Subscribing to',
      conversations.length,
      'conversation channels',
    );

    conversations.forEach((conv) => {
      if (subscribedConversationsRef.current.has(conv.id)) return;

      chatLog('[Chat] Subscribing to conversation:', conv.id);
      const channel = echoRef.current.private(`conversation.${conv.id}`);

      // Handle subscription errors
      channel.error((error) => {
        console.error('[Chat] Channel subscription error:', error);
      });

      channel
        .listen('.message.new', (data) => {
          chatLog('[Chat] Received message.new event:', data);
          if (data.message) {
            // Skip if this is our own message (already added via API response)
            const senderId = String(
              data.message.sender?.id || data.message.userId,
            );
            if (senderId === String(currentUserId)) {
              chatLog('[Chat] Skipping own message from WebSocket');
              return;
            }

            // The channel is per-conversation, so its id — not the payload's — is what
            // this event belongs to
            const messageConvId = String(conv.id);
            const selectedConvId = String(
              selectedConversationRef.current?.id || '',
            );
            const isSelectedConversation =
              selectedConvId && selectedConvId === messageConvId;
            const isUrgent = data.message.isUrgent || data.message.is_urgent;

            // Actively viewing = same conversation + chat page + tab visible + window focused
            const isActivelyViewingConversation = isActivelyViewing(
              selectedConvId,
              messageConvId,
            );

            // Check if this conversation is muted
            const messageConversation = conversationsRef.current?.find(
              (c) => String(c.id) === messageConvId,
            );
            const mutedUntil =
              messageConversation?.currentUserParticipant?.mutedUntil;
            const isMuted = mutedUntil && new Date(mutedUntil) > new Date();

            // Play appropriate notification sound (unless muted)
            if (!isMuted) {
              if (isUrgent) {
                playUrgentSound();
                // Show fullscreen urgent message
                setUrgentMessage(data.message);
              } else if (isActivelyViewingConversation) {
                playInChatSound();
              } else {
                playNotificationSound();
              }
            }

            // Add message to list if it's for the selected conversation (deduped against
            // the list). While the user is reading old history the list is not anchored
            // to the newest message, so a live message would land next to messages from
            // a completely different time — count it for the "jump to latest" badge
            // instead and let it appear when they return to the bottom.
            if (isSelectedConversation) {
              if (isAtLatestRef.current) {
                setMessages((prev) =>
                  mergeMessages(prev, [data.message], 'prepend'),
                );
              } else {
                setPendingNewCount((count) => count + 1);
              }
            }

            // Update conversation list (use string comparison for IDs)
            setConversations((prev) =>
              prev.map((c) => {
                if (String(c.id) === messageConvId) {
                  return {
                    ...c,
                    latestMessage: data.message,
                    lastMessageAt: data.message.createdAt,
                    // Always count it — the server counts it too, so suppressing it here
                    // only hid the badge locally until the next poll put it back. The
                    // auto-read timer clears it (here and on the server) shortly after.
                    unreadCount: (c.unreadCount || 0) + 1,
                  };
                }
                return c;
              }),
            );

            // Show notification popup if NOT actively viewing the conversation
            // unreadTotal is computed automatically via useMemo from conversations
            if (!isActivelyViewingConversation) {
              showNotification(data.message, messageConversation);
            }
          }
        })
        .listen('.message.updated', (data) => {
          chatLog('[Chat] Received message.updated event:', data);
          if (data.message) {
            setMessages((prev) =>
              prev.map((m) => (m.id === data.message.id ? data.message : m)),
            );
          }
        })
        .listen('.message.deleted', (data) => {
          chatLog('[Chat] Received message.deleted event:', data);
          if (data.messageId) {
            setMessages((prev) => prev.filter((m) => m.id !== data.messageId));
          }
        })
        .listen('.user.typing', (data) => {
          if (String(data.userId) !== String(currentUserId)) {
            handleTypingIndicator(conv.id, data.userId, data.userName);
          }
        });

      subscribedConversationsRef.current.add(conv.id);
    });
    // Only re-run when conversation IDs change, not on every update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationIdsKey, currentUserId]);

  // Cleanup Echo and timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Clear typing timeouts
      Object.values(typingTimeoutRef.current).forEach(clearTimeout);
      typingTimeoutRef.current = {};

      // Clear notification timeouts
      notificationTimeoutsRef.current.forEach(clearTimeout);
      notificationTimeoutsRef.current.clear();

      // Disconnect Echo
      if (echoRef.current) {
        echoRef.current.disconnect();
        echoRef.current = null;
        subscribedConversationsRef.current.clear();
      }
    };
  }, []);

  return (
    <ChatClientContext.Provider
      value={{
        conversations,
        selectedConversation,
        messages,
        unreadTotal,
        isLoading,
        isLoadingConversations,
        hasMoreMessages,
        hasMoreNewer,
        loadingDirection,
        isAtLatest,
        pendingNewCount,
        pendingScrollToId,
        clearPendingScroll,
        jumpToMessage,
        returnToLatest,
        typingUsersByConversation,
        replyToMessage,
        setSelectedConversation,
        sendMessage,
        loadMoreMessages,
        loadNewerMessages,
        createConversation,
        findOrCreateDirect,
        markAsRead,
        markAllAsRead,
        sendTypingIndicator,
        handleTypingIndicator,
        refreshConversations: fetchConversations,
        notifications,
        dismissNotification,
        setReplyToMessage,
        addReaction,
        removeReaction,
        updateConversation,
        pendingFiles,
        setPendingFiles,
        urgentMessage,
        dismissUrgentMessage,
        connectionStatus,
        getEcho,
      }}
    >
      {children}
    </ChatClientContext.Provider>
  );
};

export const useChatClient = () => useContext(ChatClientContext);
